import { useEffect, useState } from "react";

import { listBranches } from "./branches";
import { useActiveBranch } from "./use-active-branch";

/**
 * Each branch's zone once read, for the rest of the tab's life. A zone changes
 * only when an admin edits the location, and a screen opened after Home should
 * start in the right zone rather than flicker from UTC to it.
 */
const known = new Map<string, string>();

/**
 * The active branch's timezone, for screens that render an appointment time
 * without loading a day roster.
 *
 * **Times belong to the branch, never to the reader's browser** (P2 Q8). A
 * cementation at 18:30 in Beirut is 18:30 to the practice booking it, whichever
 * machine is looking — and the day it falls on can differ too, which is the
 * half that turns a cosmetic problem into a wrong date.
 *
 * **`"UTC"` until the answer arrives**, deliberately: the calendar's own grid
 * falls back the same way, and a screen that rendered nothing while waiting
 * would flicker its whole list for one string.
 *
 * `GET /branches` is clinic-scoped and open to everyone — listing branches is
 * how you pick one — so a hygienist gets the same answer an admin does.
 */
export function useBranchTimezone(): string {
  return useBranchTimezoneOrNull() ?? "UTC";
}

/**
 * The same, but **null until it is known** — for a screen that must not pick
 * "today" from a guessed zone (Home and the calendar's opening day).
 */
export function useBranchTimezoneOrNull(): string | null {
  const branchId = useActiveBranch();
  const [timezone, setTimezone] = useState<string | null>(() =>
    branchId === null ? null : (known.get(branchId) ?? null),
  );

  useEffect(() => {
    if (branchId === null) return;
    setTimezone(known.get(branchId) ?? null);

    const controller = new AbortController();

    listBranches(controller.signal)
      .then((branches) => {
        for (const branch of branches) known.set(branch.id, branch.timezone);
        const here = branches.find((branch) => branch.id === branchId);
        if (here !== undefined) setTimezone(here.timezone);
      })
      .catch(() => {
        if (!controller.signal.aborted) setTimezone((current) => current ?? "UTC");
        // UTC it is. A time an hour out is worth less than a screen that will
        // not draw, and the calendar is where times are read in earnest.
      });

    return () => controller.abort();
  }, [branchId]);

  return timezone;
}
